/**
 * /search page analytics — MVP-C (spec § 4.2 search-page parcel).
 *
 * Loaded only on /search via sections/main-search.liquid. Fires the 8
 * § 4.2 events by binding delegated listeners on `document` (persistent
 * across the theme's AJAX facet re-renders that swap #ProductGridContainer
 * innerHTML) and augmenting the existing tab-change hook.
 *
 * All fires guarded with try/catch — analytics failure never blocks the
 * search UX. Click fires run in CAPTURE PHASE so envelopes land in
 * window.dataLayer before the <a> default navigation.
 *
 * Depends on window.AroSearchAPI.trackAroSearchEvent (native-search-api.js).
 * Silently no-ops if the helper isn't loaded.
 */
(function () {
  'use strict';

  function api() {
    return (window.AroSearchAPI && typeof window.AroSearchAPI.trackAroSearchEvent === 'function')
      ? window.AroSearchAPI
      : null;
  }
  function fire(eventType, payload) {
    var a = api();
    if (!a) return;
    try { a.trackAroSearchEvent(eventType, payload); } catch (e) { /* silent */ }
  }

  // --- Search-term helper --------------------------------------------------
  function currentSearchTerm() {
    try {
      var p = new URLSearchParams(location.search);
      return (p.get('q') || '').trim() || null;
    } catch (e) { return null; }
  }

  // Serialise the CURRENT URL's filter params to a compact JSON string.
  // Skips q + sort_by + page (not "filters"). Preserves multi-value
  // params by grouping into arrays. Used by search_filter_toggle +
  // search_clear_all so the report can reconstruct filter state at
  // event time without joining back to page URLs.
  function activeFiltersJson() {
    try {
      var p = new URLSearchParams(location.search);
      var out = {};
      p.forEach(function (v, k) {
        if (k === 'q' || k === 'sort_by' || k === 'page' || k === 'type') return;
        if (!/^filter\./.test(k)) return;
        if (out[k] === undefined) out[k] = v;
        else if (Array.isArray(out[k])) out[k].push(v);
        else out[k] = [out[k], v];
      });
      return JSON.stringify(out);
    } catch (e) { return null; }
  }

  // Count products currently rendered in the products grid. Reads DOM
  // rather than search.results.size Liquid var so it reflects the
  // active per-page slice + post-filter-render state.
  function productCount() {
    var grid = document.getElementById('product-grid');
    if (!grid) return 0;
    return grid.querySelectorAll(':scope > li.columns, :scope > li:not(.loading-overlay)').length;
  }

  // Best-effort entry_source from document.referrer + URL context.
  //   header       → came from a header dropdown submit (previous page = same site + ref query params)
  //   direct-nav   → direct URL, no referrer
  //   internal     → came from another aromantic.co.uk page (collection, PDP, blog, etc.)
  //   external     → came from a different domain (Google, social, etc.)
  //   recent-click → came from clicking a recent-searches link within this session
  function entrySource() {
    try {
      // Session-scoped hint set by recent-search click handlers (MVP-C follow-up)
      var hint = sessionStorage.getItem('aro-search-page-entry-hint');
      if (hint) {
        sessionStorage.removeItem('aro-search-page-entry-hint');
        return hint;
      }
      if (!document.referrer) return 'direct-nav';
      var ref = new URL(document.referrer);
      if (ref.host !== location.host) return 'external';
      // Same host — was the previous page also /search? Then it's an
      // in-page nav (facet click / paginate). Not a "new search entry".
      if (ref.pathname === '/search') return 'in-page-nav';
      return 'internal';
    } catch (e) { return null; }
  }

  // Phase F.1 (FR-216): capture top-3 URLs from #product-grid and the
  // blogs/recipes grids so intent-level reports can answer "what did the
  // user see leading up to their decision". Captured at page load time,
  // reflecting what Shopify server-rendered on initial paint (may drift
  // slightly if AJAX facet-refresh has run since — acceptable, the intent
  // event carries a snapshot of the moment we surfaced results, and other
  // raw events (filter_toggle etc.) mark subsequent state changes).
  function topProductsFromGrid() {
    var grid = document.getElementById('product-grid');
    if (!grid) return [];
    var items = grid.querySelectorAll(':scope > li.columns, :scope > li:not(.loading-overlay)');
    var out = [];
    for (var i = 0; i < items.length && out.length < 3; i++) {
      var anchor = items[i].querySelector('a[href*="/products/"]');
      if (!anchor) continue;
      var href = anchor.getAttribute('href') || '';
      var match = href.match(/\/products\/([^/?#]+)/);
      if (match) out.push({ handle: match[1], position: i });
    }
    return out;
  }

  function topArticlesFromGrids() {
    // Blogs + Recipes grids may be present depending on template layout.
    // Concatenate first 3 across both, in DOM order (blogs first, then recipes).
    var out = [];
    var grids = document.querySelectorAll('.aro-search-page__blogs-grid, .aro-search-page__recipes-grid');
    for (var g = 0; g < grids.length && out.length < 3; g++) {
      var grid = grids[g];
      var panel = grid.classList.contains('aro-search-page__recipes-grid') ? 'recipes' : 'blogs';
      var items = grid.querySelectorAll(':scope > li');
      for (var i = 0; i < items.length && out.length < 3; i++) {
        var anchor = items[i].querySelector('a[href*="/blogs/"]');
        if (!anchor) continue;
        out.push({
          url: anchor.getAttribute('href') || null,
          panel: panel,
          position: i
        });
      }
    }
    return out;
  }

  // --- 1. search_page_view -------------------------------------------------
  // Fires once per page load, only if q param present. Guard against
  // firing on facet-only URL changes (theme uses AJAX; window.location
  // updates via pushState but this script only runs once on real page load).
  function firePageView() {
    var term = currentSearchTerm();
    if (!term) return;
    fire('search_page_view', {
      search_term: term,
      product_count: productCount(),
      entry_source: entrySource(),
      top_products: topProductsFromGrid(),
      top_articles: topArticlesFromGrids()
    });
  }

  // --- 2. search_filter_toggle --------------------------------------------
  // Shopify's FacetFiltersForm binds change handlers on facet inputs; we
  // observe via delegated change listener on document. Fires before the
  // theme's AJAX refetch — we don't wait for the render round-trip.
  document.addEventListener('change', function (e) {
    var inp = e.target;
    if (!inp || inp.tagName !== 'INPUT') return;
    // Shopify facet inputs have names like `filter.p.tag`, `filter.v.price.gte`, etc.
    var name = inp.name || '';
    if (!/^filter\./.test(name)) return;
    // Only fire from facet forms (facets-desktop / sticky-sort-filter / facets-mobile)
    if (!inp.closest('facet-filters-form, .aro-sf__popup, facets-form, form#FacetFiltersForm')) return;
    // Find filter group label — the surrounding details.thb-filter has a summary
    var groupTitle = null;
    var group = inp.closest('.thb-filter, .aro-sf__group');
    if (group) {
      var titleEl = group.querySelector('.thb-filter-title, .aro-sf__group-label, summary');
      if (titleEl) groupTitle = (titleEl.textContent || '').trim().split(/\s{2,}/)[0].split('\n')[0].trim();
    }
    // Value: input's value, fall back to label text
    var value = inp.value;
    if (!value || value === 'on') {
      var lbl = inp.id ? document.querySelector('label[for="' + inp.id + '"]') : inp.closest('label');
      if (lbl) value = (lbl.textContent || '').trim();
    }
    fire('search_filter_toggle', {
      search_term: currentSearchTerm(),
      filter_name: groupTitle,
      filter_value: value || null,
      filter_param: name,
      state: inp.checked ? 'on' : 'off',
      active_filters_json: activeFiltersJson()
    });
  }, true);

  // --- 3. search_tab_switch ------------------------------------------------
  // Track the currently-checked tab in a module var; fire on change with from/to.
  var currentTab = null;
  function readCurrentTab() {
    var r = document.querySelector('input[name="aro-search-page-tab"]:checked');
    if (r) return r.id.replace('aro-search-page-tab-', '');
    return null;
  }
  document.addEventListener('change', function (e) {
    var r = e.target;
    if (!r || r.name !== 'aro-search-page-tab' || !r.checked) return;
    var to = r.id.replace('aro-search-page-tab-', '');
    var from = currentTab;
    currentTab = to;
    if (from === to) return;
    fire('search_tab_switch', {
      search_term: currentSearchTerm(),
      from_tab: from,
      to_tab: to
    });
  }, false);

  // --- 4. search_product_click --------------------------------------------
  // 5. search_article_click
  // 6. search_chip_removed
  // 7. search_did_you_mean_click
  // 8. search_clear_all
  //
  // All bound in one delegated capture-phase click listener. Order of
  // checks matters — narrower selectors first.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    // 7. search_did_you_mean_click — narrow selector, before article check
    var dym = e.target.closest('.aro-did-you-mean__link');
    if (dym) {
      var suggested = (dym.textContent || '').replace(/["'“”‘’]/g, '').trim();
      fire('search_did_you_mean_click', {
        search_term: currentSearchTerm(),
        suggested_term: suggested
      });
      return;
    }

    // 8. search_clear_all — the "Clear all" chip inside .active-facets
    var clearAll = e.target.closest('.active-facets__button-remove');
    if (clearAll) {
      fire('search_clear_all', {
        search_term: currentSearchTerm(),
        active_filters_json: activeFiltersJson()
      });
      return;
    }

    // 6. search_chip_removed — an .active-facets__button that's NOT the clear-all
    var chip = e.target.closest('.active-facets__button:not(.active-facets__button-remove)');
    if (chip) {
      // Chip text is like "In stock ✕" or "Essential Oils ✕". Strip the ✕/svg.
      var label = (chip.textContent || '').replace(/[×✕✖]/g, '').trim();
      // Split on filter-name : filter-value if Shopify labelled it that way.
      // Chip href = removal URL (value.url_to_remove) so we can't parse
      // filter_param from it reliably. Send label as filter_value + null
      // filter_name — downstream reports can back-fill via URL diff.
      fire('search_chip_removed', {
        search_term: currentSearchTerm(),
        filter_name: null,
        filter_value: label || null
      });
      return;
    }

    // 4. search_product_click — <a> click inside #product-grid
    var grid = e.target.closest('#product-grid, .aro-search-page__products-grid');
    if (grid) {
      var anchor = e.target.closest('a[href*="/products/"]');
      if (anchor) {
        var cards = Array.prototype.slice.call(grid.children);
        var card = anchor.closest('product-card') || anchor.closest('li.columns') || anchor.closest('.product-card');
        var pos = card ? cards.indexOf(card) : -1;
        var href = anchor.getAttribute('href') || '';
        var match = href.match(/\/products\/([^/?#]+)/);
        fire('search_product_click', {
          search_term: currentSearchTerm(),
          product_handle: match ? match[1] : null,
          product_id: null,
          position: pos,
          panel: 'products'
        });
        return;
      }
    }

    // 5. search_article_click — <a> click inside blogs or recipes grid
    var articleGrid = e.target.closest('.aro-search-page__blogs-grid, .aro-search-page__recipes-grid');
    if (articleGrid) {
      var anchor2 = e.target.closest('a[href*="/blogs/"]');
      if (anchor2) {
        var cards2 = Array.prototype.slice.call(articleGrid.children);
        var card2 = anchor2.closest('li.columns') || anchor2.closest('article');
        var pos2 = card2 ? cards2.indexOf(card2) : -1;
        var panel = articleGrid.classList.contains('aro-search-page__recipes-grid') ? 'recipes' : 'blogs';
        fire('search_article_click', {
          search_term: currentSearchTerm(),
          article_url: anchor2.getAttribute('href') || null,
          panel: panel,
          position: pos2
        });
        return;
      }
    }
  }, true); // capture phase — before default navigation

  // --- Boot ---------------------------------------------------------------
  // Wait up to ~3s for window.AroSearchAPI to appear before firing the
  // page_view event. Defer-script load order should already guarantee
  // native-search-api.js runs first (it's in <head>, we're inside a body
  // section), but browser-verified evidence 2026-08-12 shows that isn't
  // always true — firePageView silently no-op'd on first load in Edge
  // while later user-interaction fires worked. Poll + retry closes the
  // race without changing script load order.
  function whenApiReady(cb) {
    if (api()) return cb();
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (api()) { clearInterval(iv); cb(); }
      else if (tries > 60) { clearInterval(iv); } // ~3s cap; give up silently
    }, 50);
  }

  function init() {
    currentTab = readCurrentTab();
    whenApiReady(firePageView);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
